import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Minus, Plus, ChevronLeft, Truck, RefreshCw, Shield } from 'lucide-react';
import axios from 'axios';
import { Layout } from '../components/layout/Layout';
import { useCart } from '../context/CartContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductDetailPage = () => {
    const { id } = useParams();
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedSize, setSelectedSize] = useState(null);
    const [selectedColor, setSelectedColor] = useState(null);
    const [quantity, setQuantity] = useState(1);
    const [wishlisted, setWishlisted] = useState(false);
    const [activeImage, setActiveImage] = useState(null);
    const { addToCart } = useCart();

    useEffect(() => {
        const fetchProduct = async () => {
            setLoading(true);
            try {
                const response = await axios.get(`${API}/products/${id}`);
                setProduct(response.data);
                if (response.data.sizes?.length > 0) {
                    setSelectedSize(response.data.sizes[0]);
                }
                if (response.data.colors?.length > 0) {
                    setSelectedColor(response.data.colors[0]);
                }
                setActiveImage(response.data.image_url || null);
            } catch (error) {
                console.error('Error fetching product:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchProduct();
    }, [id]);


    const colorMap = {
        'black': '#000000', 'white': '#FFFFFF', 'navy': '#001F5B',
        'grey': '#808080', 'gray': '#808080', 'green': '#2D5016',
        'olive': '#6B7C3C', 'brown': '#5C3317', 'red': '#CC0000',
        'blue': '#0066CC', 'yellow': '#FFD700', 'orange': '#FF6600',
        'pink': '#FF69B4', 'purple': '#6600CC', 'cream': '#FFFDD0',
        'beige': '#F5F5DC', 'maroon': '#800000', 'teal': '#008080',
    };
    const handleAddToCart = async () => {
        if (product.sizes?.length > 0 && !selectedSize) {
            toast.error('Please select a size');
            return;
        }
        await addToCart(product, quantity, selectedSize);
        toast.success('Added to cart!');
    };

    if (loading) {
        return (
            <Layout>
                <div className="pt-32 md:pt-40 pb-16 min-h-screen">
                    <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="aspect-square bg-card animate-pulse" />
                            <div className="space-y-4">
                                <div className="h-8 bg-card animate-pulse w-3/4" />
                                <div className="h-6 bg-card animate-pulse w-1/4" />
                                <div className="h-24 bg-card animate-pulse" />
                            </div>
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    if (!product) {
        return (
            <Layout>
                <div className="pt-32 md:pt-40 pb-16 min-h-screen flex items-center justify-center">
                    <div className="text-center">
                        <h1 className="font-heading text-4xl mb-4">Product Not Found</h1>
                        <Link to="/products" className="text-lime underline">Back to products</Link>
                    </div>
                </div>
            </Layout>
        );
    }

    const discount = product.original_price 
        ? Math.round((1 - product.price / product.original_price) * 100) 
        : 0;

    return (
        <Layout>
            <div className="pt-32 md:pt-40 pb-16" data-testid="product-detail-page">
                <div className="max-w-[1400px] mx-auto px-4 md:px-8">
                    {/* Breadcrumb */}
                    <Link 
                        to="/products" 
                        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-lime mb-8 transition-colors"
                        data-testid="back-link"
                    >
                        <ChevronLeft className="w-4 h-4" />
                        BACK TO PRODUCTS
                    </Link>

                    <div className="grid md:grid-cols-2 gap-8 lg:gap-16">
                        {/* Image Carousel */}
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="relative"
                        >
                            <div className="relative aspect-square bg-card border border-border overflow-hidden">
                                <div className="absolute inset-0 bg-gradient-to-br from-indigo-950/50 to-zinc-900" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    {activeImage || product.image_url ? (
                                        <img src={activeImage || product.image_url} alt={product.name} className="w-full h-full object-cover" />
                                    ) : (
                                        <span className="text-[150px] opacity-50">
                                            {product.category === 'tshirts' ? '👕' : 
                                             product.category === 'hoodies' ? '🧥' :
                                             product.category === 'bottoms' ? '👖' :
                                             product.category === 'shirts' ? '👔' : '🎒'}
                                        </span>
                                    )}
                                </div>
                            {product.badge && (
                                <div className={`absolute top-4 left-4 px-3 py-1.5 text-xs font-bold tracking-[0.1em] uppercase ${
                                    product.badge === 'SALE' ? 'bg-rose-500 text-white' :
                                    product.badge === 'HOT' ? 'bg-lime text-black' : 'bg-white text-black'
                                }`}>
                                    {product.badge}
                                </div>
                            )}
                            <button
                                onClick={() => setWishlisted(!wishlisted)}
                                className={`absolute top-4 right-4 w-10 h-10 flex items-center justify-center transition-colors ${
                                    wishlisted ? 'bg-lime text-black' : 'bg-black/50 text-white hover:bg-lime hover:text-black'
                                }`}
                                data-testid="wishlist-btn"
                            >
                                <Heart className={`w-5 h-5 ${wishlisted ? 'fill-current' : ''}`} />
                            </button>
                            </div>
                            {/* Color Image Thumbnails */}
                            {product.color_images && Object.keys(product.color_images).length > 0 && (
                                <div className="flex gap-2 mt-3 overflow-x-auto pb-1">
                                    {product.image_url && (
                                        <button onClick={() => setActiveImage(product.image_url)}
                                            className={`flex-shrink-0 w-16 h-16 border-2 overflow-hidden transition-all ${activeImage === product.image_url ? 'border-lime' : 'border-border hover:border-lime'}`}>
                                            <img src={product.image_url} alt="main" className="w-full h-full object-cover" />
                                        </button>
                                    )}
                                    {Object.entries(product.color_images).map(([color, url]) => (
                                        <button key={color} onClick={() => { setActiveImage(url); setSelectedColor(color); }}
                                            className={`flex-shrink-0 w-16 h-16 border-2 overflow-hidden transition-all ${activeImage === url ? 'border-lime' : 'border-border hover:border-lime'}`}
                                            title={color}>
                                            <img src={url} alt={color} className="w-full h-full object-cover" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </motion.div>

                        {/* Details */}
                        <motion.div
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="flex flex-col"
                        >
                            <span className="text-xs tracking-[0.2em] uppercase text-lime mb-2">
                                {product.category}
                            </span>
                            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl mb-4" data-testid="product-name">
                                {product.name}
                            </h1>
                            
                            {/* Price */}
                            <div className="flex items-center gap-4 mb-6">
                                <span className="font-heading text-3xl" data-testid="product-price">
                                    ₹{product.price.toLocaleString()}
                                </span>
                                {product.original_price && (
                                    <>
                                        <span className="text-lg text-muted-foreground line-through">
                                            ₹{product.original_price.toLocaleString()}
                                        </span>
                                        <span className="bg-lime text-black px-2 py-1 text-xs font-bold">
                                            {discount}% OFF
                                        </span>
                                    </>
                                )}
                            </div>

                            <p className="text-muted-foreground leading-relaxed mb-8">
                                {product.description}
                            </p>

                            {/* Colors */}
                            {product.colors && product.colors.length > 0 && (
                                <div className="mb-6">
                                    <span className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                                        COLOR: <span className="text-foreground">{selectedColor}</span>
                                    </span>
                                    <div className="flex gap-2">
                                        {product.colors.map((color) => (
                                            <button
                                                key={color}
                                                onClick={() => { setSelectedColor(color); if (product.color_images?.[color]) setActiveImage(product.color_images[color]); }}
                                                title={color}
                                                className={`flex flex-col items-center gap-1`}
                                                data-testid={`color-${color}`}
                                            >
                                                <span className={`w-8 h-8 rounded-full border-2 block transition-all ${selectedColor === color ? 'border-lime scale-110' : 'border-border hover:border-lime'}`} style={{ backgroundColor: colorMap[color.toLowerCase()] || '#555' }} />
                                                <span className={`text-[10px] ${selectedColor === color ? 'text-lime' : 'text-muted-foreground'}`}>{color}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Sizes */}
                            {product.sizes && product.sizes.length > 0 && (
                                <div className="mb-6">
                                    <span className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                                        SIZE: <span className="text-foreground">{selectedSize}</span>
                                    </span>
                                    <div className="flex gap-2" data-testid="size-options">
                                        {product.sizes.map((size) => (
                                            <button
                                                key={size}
                                                onClick={() => setSelectedSize(size)}
                                                className={`w-12 h-12 text-xs font-bold tracking-wide border transition-colors ${
                                                    selectedSize === size
                                                        ? 'border-lime bg-lime text-black'
                                                        : 'border-border hover:border-white'
                                                }`}
                                                data-testid={`size-${size}`}
                                            >
                                                {size}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Quantity */}
                            <div className="mb-8">
                                <span className="text-xs tracking-[0.15em] uppercase text-muted-foreground mb-3 block">
                                    QUANTITY
                                </span>
                                <div className="flex items-center border border-border w-fit">
                                    <button
                                        onClick={() => setQuantity(Math.max(1, quantity - 1))}
                                        className="w-12 h-12 flex items-center justify-center hover:bg-card transition-colors"
                                        data-testid="qty-minus"
                                    >
                                        <Minus className="w-4 h-4" />
                                    </button>
                                    <span className="w-12 h-12 flex items-center justify-center font-bold" data-testid="qty-value">
                                        {quantity}
                                    </span>
                                    <button
                                        onClick={() => setQuantity(quantity + 1)}
                                        className="w-12 h-12 flex items-center justify-center hover:bg-card transition-colors"
                                        data-testid="qty-plus"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>

                            {/* Add to Cart */}
                            <button
                                onClick={handleAddToCart}
                                className="w-full bg-lime text-black py-4 text-sm font-bold tracking-[0.14em] uppercase hover:tracking-[0.2em] transition-all duration-300 mb-6"
                                data-testid="add-to-cart-btn"
                            >
                                ADD TO CART — ₹{(product.price * quantity).toLocaleString()}
                            </button>

                            <Link
                                to="/customize"
                                className="w-full border border-border py-4 text-sm font-medium tracking-[0.14em] uppercase text-center hover:border-lime hover:text-lime transition-colors mb-8"
                                data-testid="customize-link"
                            >
                                WANT TO CUSTOMIZE? UPLOAD YOUR DESIGN
                            </Link>

                            {/* Trust badges */}
                            <div className="grid grid-cols-3 gap-4 pt-6 border-t border-border">
                                <div className="text-center">
                                    <Truck className="w-5 h-5 mx-auto mb-2 text-lime" />
                                    <span className="text-[10px] tracking-wide uppercase text-muted-foreground">Free Shipping</span>
                                </div>
                                <div className="text-center">
                                    <RefreshCw className="w-5 h-5 mx-auto mb-2 text-lime" />
                                    <span className="text-[10px] tracking-wide uppercase text-muted-foreground">15-Day Returns</span>
                                </div>
                                <div className="text-center">
                                    <Shield className="w-5 h-5 mx-auto mb-2 text-lime" />
                                    <span className="text-[10px] tracking-wide uppercase text-muted-foreground">Secure Payment</span>
                                </div>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};
